import { Contact, IContact } from '@app/models/Contact'
import {
  EntityPermissions,
  IEntityPermissions,
} from '@app/models/EntityPermissions'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IGuestViewState {
  readonly error: string
  readonly isLoading: boolean
  readonly item: IContact
  readonly permissions: IEntityPermissions
  readonly showConfirmDelete: boolean
}

const GuestViewRecord = Record({
  error: '',
  isLoading: false,
  item: new Contact(),
  permissions: new EntityPermissions(),
  showConfirmDelete: false,
})

export class GuestViewState extends GuestViewRecord implements IGuestViewState {
  public static fromJS(initialGuestView?: Partial<IGuestViewState>) {
    return new GuestViewState(initialGuestView)
  }

  public readonly error: string
  public readonly isLoading: boolean
  public readonly item: Contact
  public readonly permissions: EntityPermissions
  public readonly showConfirmDelete: boolean

  constructor(props?: Partial<IGuestViewState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  LoadGuest = 'GUEST_VIEW::LOAD_GUEST',
  LoadGuestRequest = 'GUEST_VIEW::LOAD_GUEST_REQUEST',
  LoadGuestSuccess = 'GUEST_VIEW::LOAD_GUEST_SUCCESS',
  LoadGuestFailure = 'GUEST_VIEW::LOAD_GUEST_FAILURE',
  ResetGuestView = 'GUEST_VIEW::RESET_STATE',
  SetScope = 'GUEST_VIEW::SET_SCOPE',
  ToggleConfirmDelete = 'GUEST_VIEW::TOGGLE_CONFIRM_DELETE',
}

interface ILoadGuestAction {
  type: ActionTypes.LoadGuest
  guestId: string
}

interface ILoadGuestFailureAction {
  error: string
  type: ActionTypes.LoadGuestFailure
}

interface ILoadGuestRequestAction {
  type: ActionTypes.LoadGuestRequest
}

interface ILoadGuestSuccessAction {
  item: Contact
  permissions: EntityPermissions
  type: ActionTypes.LoadGuestSuccess
}

interface IResetGuestViewAction {
  type: ActionTypes.ResetGuestView
}

interface IToggleConfirmDeleteAction {
  show: boolean
  type: ActionTypes.ToggleConfirmDelete
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadGuestFailureAction
  | ILoadGuestRequestAction
  | ILoadGuestSuccessAction
  | IResetGuestViewAction
  | IToggleConfirmDeleteAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadGuest(guestId: string): ILoadGuestAction {
  return { type: ActionTypes.LoadGuest, guestId }
}

function loadGuestFailure(error: string): ILoadGuestFailureAction {
  return { error, type: ActionTypes.LoadGuestFailure }
}

function loadGuestRequest(): ILoadGuestRequestAction {
  return { type: ActionTypes.LoadGuestRequest }
}

function loadGuestSuccess(
  item: Contact,
  permissions: EntityPermissions,
): ILoadGuestSuccessAction {
  return {
    item,
    permissions,
    type: ActionTypes.LoadGuestSuccess,
  }
}

function resetGuestView(): IResetGuestViewAction {
  return { type: ActionTypes.ResetGuestView }
}

function toggleConfirmDelete(show: boolean): IToggleConfirmDeleteAction {
  return {
    show,
    type: ActionTypes.ToggleConfirmDelete,
  }
}

export const actionCreators = {
  loadGuest,
  resetGuestView,
  toggleConfirmDelete,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<GuestViewState> = (
  state: GuestViewState = new GuestViewState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.LoadGuestFailure:
      return state
        .set('isLoading', false)
        .set('error', action.error) as GuestViewState

    case ActionTypes.LoadGuestRequest:
      return state.set('isLoading', true) as GuestViewState

    case ActionTypes.LoadGuestSuccess:
      return state
        .set('isLoading', false)
        .set('item', action.item)
        .set('permissions', action.permissions) as GuestViewState

    case ActionTypes.ResetGuestView:
      return state.set('isLoading', false) as GuestViewState

    case ActionTypes.ToggleConfirmDelete:
      return state.set('showConfirmDelete', action.show) as GuestViewState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadGuestSaga(action: ILoadGuestAction) {
  try {
    yield put(loadGuestRequest())

    const baseUrl = `/api/Guests/${action.guestId}`

    const result = yield makeAuthenticatedRequest(baseUrl)

    if (result.success === true) {
      const entity = result.entity as Contact

      yield put(
        loadGuestSuccess(
          Contact.fromJS(entity),
          EntityPermissions.fromJS(result.permissions),
        ),
      )
    } else {
      yield put(loadGuestFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadGuest() {
  yield takeLatest(ActionTypes.LoadGuest, loadGuestSaga)
}

export function* guestViewRootSaga() {
  yield fork(watchLoadGuest)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): GuestViewState =>
  state.guestView

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItem = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.item,
  )

export const selectPermissions = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.permissions,
  )

export const selectShowConfirmDelete = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.showConfirmDelete,
  )
